<template>
	<button
		v-tooltip.top="addedToList ? 'Αφαίρεση από τη λίστα μου' : 'Προσθήκη στη λίστα μου'"
		class="btn--circle add-to-list"
		:class="{ active: addedToList }"
		@click="addItemToList"
		v-focus
	>
		<IconDefaultAddToList v-if="!addedToList" />
		<IconActiveAddToList v-else />
		<Transition name="fade">
			<div v-if="showActionMessage" class="action-message">
				<p class="list-btn-action" v-if="addedToList">{{ $t("innerpage.added") }}</p>
				<p class="list-btn-action" v-else>{{ $t("innerpage.removed") }}</p>
			</div>
		</Transition>
	</button>
</template>

<script setup lang="ts">
	import { computed, onBeforeUnmount, onMounted, Ref, ref } from "vue"
	import IconDefaultAddToList from "../../icons/IconPlus.vue"
	import IconActiveAddToList from "../../icons/IconCheckmark.vue"
	import { Movie } from "../../types/Movie"
	import { Series } from "../../types/Series"
	import { useUserProfiles } from "../../composables/useUserProfile"
	import { useMovieStore } from "../../stores/movies"
	import { useSeriesStore } from "../../stores/series"
	import { profileList } from "../../composables/useListItems"
	const { fetchVideoFlagStoreByProfile, storeStartedEntity } = useUserProfiles()
	const props = defineProps<{
		item: Movie | Series
		type: string
	}>()
	const entityType: Ref<string> = ref("")
	const showActionMessage: Ref<boolean> = ref(false)
	const addedToList: Ref<boolean> = ref(false)
	const moviesStore = useMovieStore()
	const seriesStore = useSeriesStore()
	const { addToProfileList, removeFromProfileList } = profileList()

	/*
	 * Check if item is already in user's list
	 */
	const itemExists = async () => {
		if (props.type === "node--series") {
			entityType.value = "series"
		} else {
			entityType.value = "movie"
		}
		await fetchVideoFlagStoreByProfile(props.item.id, entityType.value, "favourite", undefined).then((res) => {
			const exists = res[0]?.id
			if (exists) {
				addedToList.value = true
			} else {
				addedToList.value = false
			}
		})
	}

	/*
	 * Toggle add to my list state
	 */
	const addItemToList = async (): Promise<void> => {
		if (props.type === "node--series") {
			entityType.value = "series"
		} else {
			entityType.value = "movie"
		}
		// console.log("Toggling favourite for", props.item.id, "of type", entityType.value)
		const res = await storeStartedEntity("favourite", entityType.value, undefined, props.item.id, undefined)
		if (res === "deleted") {
			addedToList.value = false
			if (props.type === "series") {
				removeFromProfileList(seriesStore.state.profileSeriesList, props.item, 45)
			} else {
				removeFromProfileList(moviesStore.state.profileMoviesList, props.item, 45)
			}
		} else if (res === "created") {
			addedToList.value = true

			if (props.type === "series") {
				addToProfileList(seriesStore.state.profileSeriesList, props.item, 45)
			} else {
				addToProfileList(moviesStore.state.profileMoviesList, props.item, 45)
			}
		}

		showActionMessage.value = true
		setTimeout(() => {
			showActionMessage.value = false
		}, 3000)
	}

	onMounted(async () => {
		await itemExists()
	})
	onBeforeUnmount(() => {
		entityType.value = ""
	})
</script>

<style lang="scss" scoped>
	.add-to-list {
		position: relative;
		width: 45px;
		height: 45px;
		svg {
			width: 25px;
			height: 25px;
		}
		.active {
			display: none;
		}
		.default {
			display: block;
		}
		&.active {
			.active {
				display: block;
			}
			.default {
				display: none;
			}
		}
	}
	.action-message {
		position: absolute;
		background: black;
		color: #fff;
		padding: 5px 10px;
		font-size: 14px;
		left: 50px;
		width: 80px;
		z-index: 1;
		height: 50px;
		line-height: 40px;
	}

	@keyframes hideAnimation {
		to {
			visibility: hidden;
		}
	}

	// fade transition
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.5s;
	}
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}
	.fade-enter-to,
	.fade-leave-from {
		opacity: 1;
	}
</style>
